import { useState } from 'react';
import { Archive, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Archived chats - the recovery surface for deleted conversations.
 *
 * Deleting a chat never removes it from the database any more; it only hides it
 * from the inbox. Everything hidden shows up here so an account can always get a
 * conversation back (individually or all at once) instead of losing it forever.
 */
export default function ArchivedChats({ chats = [], onRestore, onRestoreAll }) {
  const [open, setOpen] = useState(false);

  if (!chats.length) return null;

  return (
    <div className="mt-auto border-t border-line px-3 pb-[max(env(safe-area-inset-bottom),10px)] pt-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[12px] font-semibold text-ink-2 transition hover:bg-surface hover:text-accent"
        aria-expanded={open}
      >
        <Archive size={14} />
        <span className="flex-1">Archived chats</span>
        <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10.5px] font-bold text-ink-3">
          {chats.length}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      </button>

      {open && (
        <div className="mt-1 max-h-[38vh] space-y-1 overflow-y-auto pr-0.5">
          {chats.map((chat) => (
            <div
              key={chat.conversationId}
              className="rounded-lg border border-line bg-surface px-2.5 py-2"
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-semibold text-ink">
                    {chat.otherPartyDisplayName}
                  </p>
                  {chat.listingTitle && (
                    <p className="truncate text-[10.5px] text-ink-3">{chat.listingTitle}</p>
                  )}
                  {chat.lastMessagePreview && (
                    <p className="mt-0.5 line-clamp-1 text-[11px] text-ink-2">
                      {chat.lastMessagePreview}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRestore?.(chat.conversationId)}
                  className="shrink-0 rounded-md border border-line px-2 py-1 text-[10.5px] font-semibold text-ink-2 transition hover:border-brand-300 hover:bg-brand-soft hover:text-accent"
                  title="Restore this chat"
                >
                  Restore
                </button>
              </div>
            </div>
          ))}

          {chats.length > 1 && (
            <button
              type="button"
              onClick={() => onRestoreAll?.()}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-2 py-1.5 text-[11.5px] font-semibold text-white transition hover:bg-brand-700"
            >
              <RotateCcw size={12} /> Restore all {chats.length}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
